import { ReactElement, ReactNode } from 'react';
import { search } from '../helpers/search';
import { FiltersList } from '../helpers/generateFilters';
import { WMTableColumnShape, WMTableProps } from '../WMTable/WMTable';
import { CC_PERMISSIONS, ICCUser, PERM_TYPES } from '@communication-center/data-objects';
import { WMTableSortOrder } from '@walkme/wm-ui';

export type AppTableRowId = string;

export type AppTableFilter = {
    label?: string;
    value?: string;
    checked?: boolean;
};

export type AppTableFilterOption = { label: string; value: string };
export type AppTableFiltersMetadata = Record<string, AppTableFilterOption[]>;

interface IAppTableRowActionBase {
    name?: string;
    icon?: $TSFixMe;
    label?: string | ReactNode;
    isMenuItem: boolean;
    className?: string;
    restrictedTo?: string[];
    restrictedToPermissions?: [CC_PERMISSIONS, PERM_TYPES] | [CC_PERMISSIONS];
}

type Row<T> = T & IAppTableRowData;

export interface IAppTableRowAction<T = IAppTableRowData> extends IAppTableRowActionBase {
    isHidden?: (row: Row<T>) => boolean;
    handler?: (row: Row<T>, context: AppTableContextType<T>) => void;
    isDisabled?: (row: Row<T>) => boolean;
    isActive?: (row: Row<T>) => boolean;
    isDisabledByPermission?: boolean;
}

export interface IAppTableRowMultipleAction<T = IAppTableRowData> extends IAppTableRowActionBase {
    isHidden?: (rows: Row<T>[]) => boolean;
    handler?: (rows: Row<T>[], context: AppTableContextType<T>) => void;
    isDisabled?: (rows: Row<T>[]) => boolean;
    isActive?: (rows?: Row<T>[]) => boolean;
}

export interface IAppTableRowData {
    id: AppTableRowId;
    user?: ICCUser;
    fixedTop?: boolean;
}

export type TableDataSorting = {
    dataKey: string;
    direction: WMTableSortOrder;
};

export enum AppTableRowAnimateType {
    created = 'created',
    deleted = 'deleted',
    modified = 'modified',
    process = 'process',
    active = 'active'
}

export interface ITableData {
    pagination?: boolean;
    page?: number;
    limit?: number;
    sorting: TableDataSorting;
    selectedIds: Array<AppTableRowId>;
    animateIds: Map<AppTableRowId, AppTableRowAnimateType>;
    filters: Record<string, string[]>;
}

export interface IAppTableProps<T = IAppTableRowData> {
    'data-testid'?: string;
    data?: T[];
    count?: number;
    fetching?: boolean;
    filtersFields?: AppTableFiltersMetadata;
    disabled?: boolean;
    tableName?: string | ReactElement;
    hideToolbar?: boolean;
    searchTerm?: string;
    onSearchTermChange?: (str: string) => void;
    searchMethod?: typeof search;
    searchKeys?: string[];
    loadingRows?: Array<IAppTableRowData>;
    columns: WMTableColumnShape[];
    classNames?: {
        tableContainer: string;
    };
    actions?: IAppTableRowAction<T>[];
    multipleRowsActions?: IAppTableRowMultipleAction<T>[];
    tableData?: ITableData;
    onTableDataChange?: (changes: Partial<ITableData>) => void;
    environmentId: string;
    emptyTableRenderer?: WMTableProps['emptyRenderer'];
    isActiveRow?: (row: Row<T>) => boolean;
    onRowClick?: WMTableProps['rowEventHandlers'][string];
    wmTableProps?: Partial<WMTableProps>;
}

export type AppTableContextType<T = IAppTableRowData> = {
    data: T[];
    disabled?: boolean;
    tableDataTestid?: string;
    environmentId: IAppTableProps['environmentId'];
    actions?: IAppTableProps<T>['actions'];
    multipleActions?: IAppTableProps<T>['multipleRowsActions'];
    selectedIds: Set<AppTableRowId>;
    animateIds: ITableData['animateIds'];
    onTableDataChange?: (changes: Partial<ITableData>) => void;
    filtersList: FiltersList;
    setFilter: (dataKey: string, filter: AppTableFilter[]) => void;
    sorting: ITableData['sorting'];
    loading: boolean;
    addProcessingRowIds?: (ids: AppTableRowId[], type: AppTableRowAnimateType) => void;
    removeProcessingRowIds?: (ids: AppTableRowId[], type: AppTableRowAnimateType) => void;
    columns: IAppTableProps['columns'];
    tableName: IAppTableProps<T>['tableName'];
    searchTerm?: IAppTableProps<T>['searchTerm'];
    onSearchTermChange?: IAppTableProps<T>['onSearchTermChange'];
};
